import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DateInput from "@/components/date-input";
import { ScheduleTimePicker } from "@/components/schedule-time-picker";
import { localDateTimeToUTC } from "@/lib/timezone-schedule";
import type { TeacherProfile } from "@contracts/teacher";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import type { Student } from "@contracts/student";

interface LessonFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: React.ReactNode;
  studentId: string;
  student: Student;
}

export default function LessonFormDialog({ open, onOpenChange, trigger, studentId, student }: LessonFormDialogProps) {
  const { toast } = useToast();
  const { data: profile } = useQuery<TeacherProfile>({
    queryKey: ["/api/profile"],
  });
  const teacherTz = useMemo(
    () =>
      profile?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "UTC",
    [profile?.timezone]
  );

  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState(student.lessonDuration);

  useEffect(() => {
    if (open) {
      const now = new Date();
      setDate(now.toISOString().split("T")[0]);
      setDuration(student.lessonDuration);
    }
  }, [open, student]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/lessons", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/students", studentId, "debt"] });
      onOpenChange(false);
      toast({ title: "Lesson added", variant: "success" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const [yy, mo, dd] = date.split("-").map(Number);
    const [th, tm] = time.split(":").map(Number);
    const scheduledDate = localDateTimeToUTC(yy, mo, dd, th, tm, teacherTz);
    createMutation.mutate({
      studentId,
      scheduledDate: scheduledDate.toISOString(),
      duration,
      lessonType: student.lessonType,
      address: student.address,
      unitRate: student.unitRate,
      unitCurrency: student.unitCurrency,
      paymentStatus: "pending",
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-sm" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Add Lesson for {student.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lesson-date">Date</Label>
            <DateInput
              id="lesson-date"
              value={date}
              onChange={setDate}
              required
              data-testid="input-lesson-date"
            />
          </div>
          <div className="space-y-2 w-full">
            <Label className="block">Time</Label>
            <ScheduleTimePicker
              value={time}
              onChange={setTime}
              aria-label="Lesson time"
              data-testid="input-lesson-time"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lesson-dur">Duration (minutes)</Label>
            <Input
              id="lesson-dur"
              type="number"
              min="5"
              max="300"
              step="5"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 60)}
              data-testid="input-lesson-duration"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={createMutation.isPending} data-testid="button-save-lesson">
              {createMutation.isPending ? "Saving..." : "Add Lesson"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
